import { Directive, Input } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  InvestmentKnowledgeType,
  RiskProfileForm,
} from './ea-risk-profile-form.model';

@Directive()
export abstract class EaRiskProfileFormBuilder extends PrFormAbstract {
  @Input() isPersonMoral: boolean = false;

  generate(
    entity: Partial<RiskProfileForm> | null,
    options?: { isPersonMoral: boolean },
  ): UntypedFormGroup {
    const isPersonMoral = options?.isPersonMoral ?? this.isPersonMoral;
    const controlOptions: AbstractControlOptions = {};

    return this.formBuilder.group(
      {
        // ========== PERSONNE MORALE (requis si isPersonMoral) ==========
        legalStructure: [
          entity?.legalStructure ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],
        caValue: [
          entity?.caValue ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],
        dateDeCreation: [
          entity?.dateDeCreation ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],
        resultatNet: [
          entity?.resultatNet ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],
        leasing: [
          entity?.leasing ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],
        tresorerieAPlacer: [
          entity?.tresorerieAPlacer ?? null,
          isPersonMoral ? [Validators.required] : [],
        ],

        // ========== PERSONNE PHYSIQUE (requis si !isPersonMoral) ==========
        maritalStatus: [
          entity?.maritalStatus ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],
        occupancyStatus: [
          entity?.occupancyStatus ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],
        income: [
          entity?.income ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],
        workInFinance: [
          entity?.workInFinance ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],
        credit: [
          entity?.credit ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],
        readSpecializedPress: [
          entity?.readSpecializedPress ?? null,
          !isPersonMoral ? [Validators.required] : [],
        ],

        // ========== COMMUN (tous requis) ==========
        patrimonyValue: [entity?.patrimonyValue ?? null, [Validators.required]],
        riskTolerance: [entity?.riskTolerance ?? null, [Validators.required]],
        desiredYield: [entity?.desiredYield ?? null, [Validators.required]],
        conceptReturnRisk: [
          entity?.conceptReturnRisk ?? null,
          [Validators.required],
        ],
        lossTolerance: [entity?.lossTolerance ?? null, [Validators.required]],
        savingsRate: [entity?.savingsRate ?? null, [Validators.required]],
        hadInvestmentDecrease: [
          entity?.hadInvestmentDecrease ?? null,
          [Validators.required],
        ],
        investmentDecreaseReaction: [
          entity?.investmentDecreaseReaction ?? null,
          [Validators.required],
        ],
        moneyGames: [entity?.moneyGames ?? null, [Validators.required]],

        // ========== CONNAISSANCE INVESTISSEMENT ==========
        investmentKnowledge: [entity?.investmentKnowledge ?? []],

        // Sous-champs - Fonds monétaires
        monetaryProduct: [entity?.monetaryProduct ?? null],
        investMonetaryProduct: [entity?.investMonetaryProduct ?? false],
        monetaryProductManagementMode: [
          entity?.monetaryProductManagementMode ?? null,
        ],

        // Sous-champs - Obligations
        bond: [entity?.bond ?? null],
        investBond: [entity?.investBond ?? false],
        bondManagementMode: [entity?.bondManagementMode ?? null],

        // Sous-champs - Actions
        stock: [entity?.stock ?? null],
        investStock: [entity?.investStock ?? false],
        stockManagementMode: [entity?.stockManagementMode ?? null],

        // Sous-champs - SCPI
        scpi: [entity?.scpi ?? null],
        investScpi: [entity?.investScpi ?? false],
        scpiManagementMode: [entity?.scpiManagementMode ?? null],

        // Sous-champs - Capital investissement
        privateEquity: [entity?.privateEquity ?? null],
        investPrivateEquity: [entity?.investPrivateEquity ?? false],
        privateEquityManagementMode: [
          entity?.privateEquityManagementMode ?? null,
        ],

        // Sous-champs - Assurance Vie / PER
        lifeInsurance: [entity?.lifeInsurance ?? null],
        investLifeInsurance: [entity?.investLifeInsurance ?? false],
        lifeInsuranceManagementMode: [
          entity?.lifeInsuranceManagementMode ?? null,
        ],

        // Sous-champs - Produits dérivés
        derivativeProducts: [entity?.derivativeProducts ?? null],
        investDerivativeProducts: [entity?.investDerivativeProducts ?? false],
        derivativeProductsManagementMode: [
          entity?.derivativeProductsManagementMode ?? null,
        ],
      },
      controlOptions,
    );
  }

  extract(): any {
    const formValue = this.form.value;
    const investmentKnowledge: string[] = formValue.investmentKnowledge ?? [];

    // Helper to convert YES/NO string to boolean
    const yesNoToBoolean = (value: string | null): boolean => {
      return value === 'YES';
    };

    // Build the DTO - Les champs requis sont validés par le formulaire
    const dto: any = {
      // Type de personne (requis par l'API)
      type: this.isPersonMoral ? 'MORAL' : 'PHYSICAL',
    };

    // Champs personne morale (tous requis si MORAL)
    if (this.isPersonMoral) {
      dto.legalStructure = formValue.legalStructure;
      dto.caValue = formValue.caValue;
      dto.dateDeCreation = formValue.dateDeCreation;
      dto.resultatNet = formValue.resultatNet;
      dto.leasing = formValue.leasing;
      dto.tresorerieAPlacer = formValue.tresorerieAPlacer;
    } else {
      // Champs personne physique (tous requis si PHYSICAL)
      dto.maritalStatus = formValue.maritalStatus;
      dto.occupancyStatus = formValue.occupancyStatus;
      dto.income = formValue.income;
      dto.workInFinance = formValue.workInFinance;
      dto.credit = formValue.credit;
      dto.readSpecializedPress = formValue.readSpecializedPress;
    }

    // Champs communs (tous requis)
    dto.patrimonyValue = formValue.patrimonyValue;
    dto.riskTolerance = formValue.riskTolerance;
    dto.desiredYield = formValue.desiredYield;
    dto.conceptReturnRisk = formValue.conceptReturnRisk;
    dto.lossTolerance = formValue.lossTolerance;
    dto.savingsRate = formValue.savingsRate;
    dto.investmentDecreaseReaction = formValue.investmentDecreaseReaction;
    dto.moneyGames = formValue.moneyGames;

    // hadInvestmentDecrease doit être un booléen (requis)
    dto.hadInvestmentDecrease = yesNoToBoolean(formValue.hadInvestmentDecrease);

    // Investment knowledge boolean flags (toujours inclus)
    dto.knowMonetaryProduct = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_MONETARY_PRODUCT,
    );
    dto.knowBond = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_BOND,
    );
    dto.knowStock = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_STOCK,
    );
    dto.knowScpi = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_SCPI,
    );
    dto.knowPrivateEquity = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_PRIVATE_EQUITY,
    );
    dto.knowLifeInsurance = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_LIFE_INSURANCE,
    );
    dto.knowDerivativeProducts = investmentKnowledge.includes(
      InvestmentKnowledgeType.KNOW_DERIVATIVE_PRODUCTS,
    );

    // Sous-champs conditionnels selon investmentKnowledge
    if (dto.knowMonetaryProduct) {
      if (formValue.monetaryProduct)
        dto.monetaryProduct = formValue.monetaryProduct;
      dto.investMonetaryProduct = formValue.investMonetaryProduct ?? false;
      if (
        formValue.investMonetaryProduct &&
        formValue.monetaryProductManagementMode
      ) {
        dto.monetaryProductManagementMode =
          formValue.monetaryProductManagementMode;
      }
    } else {
      dto.investMonetaryProduct = false;
    }

    if (dto.knowBond) {
      if (formValue.bond) dto.bond = formValue.bond;
      dto.investBond = formValue.investBond ?? false;
      if (formValue.investBond && formValue.bondManagementMode) {
        dto.bondManagementMode = formValue.bondManagementMode;
      }
    } else {
      dto.investBond = false;
    }

    if (dto.knowStock) {
      if (formValue.stock) dto.stock = formValue.stock;
      dto.investStock = formValue.investStock ?? false;
      if (formValue.investStock && formValue.stockManagementMode) {
        dto.stockManagementMode = formValue.stockManagementMode;
      }
    } else {
      dto.investStock = false;
    }

    if (dto.knowScpi) {
      if (formValue.scpi) dto.scpi = formValue.scpi;
      dto.investScpi = formValue.investScpi ?? false;
      if (formValue.investScpi && formValue.scpiManagementMode) {
        dto.scpiManagementMode = formValue.scpiManagementMode;
      }
    } else {
      dto.investScpi = false;
    }

    if (dto.knowPrivateEquity) {
      if (formValue.privateEquity) dto.privateEquity = formValue.privateEquity;
      dto.investPrivateEquity = formValue.investPrivateEquity ?? false;
      if (
        formValue.investPrivateEquity &&
        formValue.privateEquityManagementMode
      ) {
        dto.privateEquityManagementMode = formValue.privateEquityManagementMode;
      }
    } else {
      dto.investPrivateEquity = false;
    }

    if (dto.knowLifeInsurance) {
      if (formValue.lifeInsurance) dto.lifeInsurance = formValue.lifeInsurance;
      dto.investLifeInsurance = formValue.investLifeInsurance ?? false;
      if (
        formValue.investLifeInsurance &&
        formValue.lifeInsuranceManagementMode
      ) {
        dto.lifeInsuranceManagementMode = formValue.lifeInsuranceManagementMode;
      }
    } else {
      dto.investLifeInsurance = false;
    }

    if (dto.knowDerivativeProducts) {
      if (formValue.derivativeProducts)
        dto.derivativeProducts = formValue.derivativeProducts;
      dto.investDerivativeProducts =
        formValue.investDerivativeProducts ?? false;
      if (
        formValue.investDerivativeProducts &&
        formValue.derivativeProductsManagementMode
      ) {
        dto.derivativeProductsManagementMode =
          formValue.derivativeProductsManagementMode;
      }
    } else {
      dto.investDerivativeProducts = false;
    }

    return dto;
  }

  /**
   * Updates form state based on current selections.
   * Resets sub-fields when parent selection changes.
   * All fields are optional to allow partial save.
   */
  protected updateValidators(): void {
    const investmentKnowledge: string[] =
      this.form.get('investmentKnowledge')?.value ?? [];

    // Reset investment knowledge sub-fields when deselected
    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_MONETARY_PRODUCT,
      investmentKnowledge,
      'monetaryProduct',
      'investMonetaryProduct',
      'monetaryProductManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_BOND,
      investmentKnowledge,
      'bond',
      'investBond',
      'bondManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_STOCK,
      investmentKnowledge,
      'stock',
      'investStock',
      'stockManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_SCPI,
      investmentKnowledge,
      'scpi',
      'investScpi',
      'scpiManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_PRIVATE_EQUITY,
      investmentKnowledge,
      'privateEquity',
      'investPrivateEquity',
      'privateEquityManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_LIFE_INSURANCE,
      investmentKnowledge,
      'lifeInsurance',
      'investLifeInsurance',
      'lifeInsuranceManagementMode',
    );

    this.resetInvestmentFieldsIfDeselected(
      InvestmentKnowledgeType.KNOW_DERIVATIVE_PRODUCTS,
      investmentKnowledge,
      'derivativeProducts',
      'investDerivativeProducts',
      'derivativeProductsManagementMode',
    );
  }

  private resetInvestmentFieldsIfDeselected(
    knowledgeType: InvestmentKnowledgeType,
    selectedKnowledge: string[],
    knowledgeField: string,
    investField: string,
    managementModeField: string,
  ): void {
    const isKnowledgeSelected = selectedKnowledge.includes(knowledgeType);
    const hasInvested = this.form.get(investField)?.value === true;

    // Reset management mode if not invested
    if (!hasInvested) {
      this.form.get(managementModeField)?.setValue(null, { emitEvent: false });
    }

    // Reset sub-fields if knowledge type is deselected
    if (!isKnowledgeSelected) {
      this.form.get(knowledgeField)?.setValue(null, { emitEvent: false });
      this.form.get(investField)?.setValue(false, { emitEvent: false });
      this.form.get(managementModeField)?.setValue(null, { emitEvent: false });
    }
  }
}
